/**
 * PromptDialog - Reużywalny dialog z input z Radix UI
 * wojskaspecjalne.pl
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  Input,
  Label,
  Textarea,
} from '../ui';

interface PromptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  placeholder?: string;
  defaultValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  onConfirm: (value: string) => void;
  onCancel?: () => void;
  loading?: boolean;
  multiline?: boolean;
  required?: boolean;
  maxLength?: number;
}

export const PromptDialog: React.FC<PromptDialogProps> = ({
  open,
  onOpenChange,
  title,
  description,
  placeholder = 'Wpisz tekst...',
  defaultValue = '',
  confirmLabel = 'Potwierdź',
  cancelLabel = 'Anuluj',
  onConfirm,
  onCancel,
  loading = false,
  multiline = false,
  required = false,
  maxLength,
}) => {
  const [value, setValue] = useState(defaultValue);

  useEffect(() => {
    if (open) {
      setValue(defaultValue);
    }
  }, [open, defaultValue]);

  const handleConfirm = () => {
    if (required && !value.trim()) {
      return;
    }
    onConfirm(value);
    if (!loading) {
      onOpenChange(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    setValue('');
    onOpenChange(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !multiline && !e.shiftKey) {
      e.preventDefault();
      handleConfirm();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="2">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label htmlFor="prompt-input">
              {required && <span className="text-red-500">* </span>}
              Twoja odpowiedź:
            </Label>
            {multiline ? (
              <Textarea
                id="prompt-input"
                placeholder={placeholder}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                maxLength={maxLength}
                rows={4}
                autoFocus
                className="mt-2"
              />
            ) : (
              <Input
                id="prompt-input"
                type="text"
                placeholder={placeholder}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={handleKeyDown}
                maxLength={maxLength}
                autoFocus
                className="mt-2"
              />
            )}
            {maxLength && (
              <p className="text-sm text-[var(--text-white-65)] mt-1">
                {value.length} / {maxLength} znaków
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={loading}
          >
            {cancelLabel}
          </Button>
          <Button
            variant="solid"
            onClick={handleConfirm}
            disabled={loading || (required && !value.trim())}
          >
            {loading ? 'Przetwarzanie...' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

